import { ListboxPopover, ListboxOption, ListboxInput, ListboxArrow, ListboxButton, ListboxList } from '@reach/listbox';
import React__default, { useMemo, Children } from 'react';
import styled from 'styled-components';
import { setTestId } from '../../lib/testId.js';
import { Button } from '../button/Button.js';
import { Chip } from '../chip/Chip.js';
import { COLOR_PALETTE } from '../colors/colorPalette.js';
import { CSSDefaultInputField } from '../fields/common.js';
import { FIELD_ERROR_DATA_ATTRIBUTE } from '../fields/FieldError.js';
import { useCheckModal } from '../modal/Modal.js';
import { getColor } from '../styles/stylePropTypes.js';
import { Text } from '../text/Text.js';
import { CSSDefaultPopover, CSSDefaultOption, InnerInputLabel } from './InputCommon.js';

const MIN_INPUT_WIDTH_REM = 13;
/**
 * DEPRECATED to make room for a new SelectInput Component which will
 * replace DEPRECATED_SelectInput.tsx and ComboBoxInput.tsx
 */ /**
 * ListboxOptions don't accept an empty value, so to provide a deselect option we must
 * match on a special string.
 */ const NO_VALUE = "NO_VALUE";
const SelectPopover = styled(ListboxPopover)`
  &[data-reach-listbox-popover] {
    ${CSSDefaultPopover}
  }

  &[data-reach-listbox-popover] > [data-hpy-web-select-field-listbox-list] {
    max-height: 14rem;
    overflow: auto;
  }
`;
const StyledListboxOption = styled(ListboxOption)`
  &[data-reach-listbox-option] {
    ${CSSDefaultOption}
  }
`;
const DEPRECATED_SelectOption = ({ label , value , testId ="X" , children , ...props })=>{
    return React__default.createElement(StyledListboxOption, {
        "data-hpy-web-select-option": true,
        ...setTestId("SelectOption-" + testId),
        ...props,
        label: label,
        value: value
    }, !!children ? children : React__default.createElement(Text, {
        weight: "semibold"
    }, label));
};
DEPRECATED_SelectOption.dataId = "data-hpy-web-select-option";
DEPRECATED_SelectOption.displayName = "SelectOption";
const StyledListboxInput = styled(ListboxInput)`
  /* width: 100%; */

  &[data-hpy-web-select-field-input-base] [data-reach-listbox-button] {
    ${CSSDefaultInputField}
    position: relative;
    display: flex;
    cursor: pointer;
    padding: 1.5rem 2.5rem 0.5rem 1rem;
    align-items: flex-start;
    min-width: ${MIN_INPUT_WIDTH_REM}rem;

    [${FIELD_ERROR_DATA_ATTRIBUTE}] > &,
    [${FIELD_ERROR_DATA_ATTRIBUTE}] > &:hover,
    [${FIELD_ERROR_DATA_ATTRIBUTE}] > &:focus {
      color: inherit;
    }
  }

  &[data-hpy-web-select-field-input-base][data-state='expanded'] [data-reach-listbox-button],
  &[data-hpy-web-select-field-input-base][data-state='expanded'] [data-reach-listbox-button]:hover,
  &[data-hpy-web-select-field-input-base] [data-reach-listbox-button]:focus {
    outline: none;
    border-color: ${(props)=>getColor("context", props.theme)};
  }
`;
const StyledListBoxArrow = styled(ListboxArrow)`
  position: absolute;
  top: 50%;
  margin-top: -2px;
  right: 1rem;
  display: block;
  width: 0px;
  height: 0px;
  content: '';
  transform: rotate(0deg);
  border-top: 5px solid ${COLOR_PALETTE.slateGrey100};
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  transition: transform 0.2s ease 0s, -webkit-transform 0.2s ease 0s;

  &[data-expanded] {
    transform: rotate(180deg);
  }
`;
const useSelectedLabel = (children, value)=>useMemo(()=>{
        let selectedChildLabel = undefined;
        Children.forEach(children, (child)=>{
            if (child && child.props.value === value) {
                selectedChildLabel = child.props.label;
            }
        });
        return selectedChildLabel;
    }, [
        children,
        value
    ]);
const hasBaseSelect = (props)=>!props.as;
const hasChip = (props)=>props?.as?.dataId === Chip.dataId;
/**
 * Select Input with Formik Field bindings.
 * Accepts `SelectOptions` as child components. If a 'deselected' option is required, it
 * must be manually added to options with a value of SELECT_OPTION_NO_VALUE.
 *
 * @example
 * <DEPRECATED_SelectInput name="chicken">
 *  <SelectOption label="" value={SELECT_OPTION_NO_VALUE} />
 *  <SelectOption label="Egg" value="egg" />
 *  <SelectOption label="Duck" value="duck" />
 * </DEPRECATED_SelectInput>
 */ const SelectInputAs = ({ label , readOnly =false , value =NO_VALUE , children , testId ="X" , as , ...props })=>{
    const isModal = useCheckModal();
    const selectedLabel = useSelectedLabel(children, value);
    let asProps = {
        testId
    };
    if (as?.dataId === Chip.dataId) {
        asProps = {
            testId,
            label,
            sublabel: selectedLabel,
            icon: props.icon
        };
    }
    if (as?.dataId === Button.dataId) {
        asProps = {
            testId,
            icon: props.icon,
            variant: props.variant
        };
    }
    return React__default.createElement(StyledListboxInput, {
        "data-hpy-web-select-field-input": true,
        ...as === undefined ? {
            "data-hpy-web-select-field-input-base": true
        } : {},
        ...setTestId("DEPRECATED_SelectInput-" + testId),
        ...props
    }, React__default.createElement(ListboxButton, {
        ...selectedLabel ? {
            "data-hpy-web-select-input-input-has-value": true
        } : {},
        ...asProps,
        ...setTestId("Button-" + testId),
        disabled: readOnly,
        as: as
    }, as === undefined && React__default.createElement(React__default.Fragment, null, React__default.createElement(Text, null, selectedLabel), React__default.createElement(StyledListBoxArrow, null, "\xa0"), React__default.createElement(InnerInputLabel, {
        "data-hpy-web-select-input-input-inner-label": true,
        size: "small"
    }, label)), as !== undefined && (!!props?.showSelectedLabel && !!selectedLabel ? selectedLabel : label)), React__default.createElement(SelectPopover, {
        portal: !isModal
    }, React__default.createElement(ListboxList, {
        "data-hpy-web-select-field-listbox-list": true
    }, children)));
};
/**
 * DEPRECATED to make room for a new SelectInput Component which will
 * replace DEPRECATED_SelectInput.tsx and ComboBoxInput.tsx
 */ SelectInputAs.dataId = "data-hpy-web-select-field";
SelectInputAs.displayName = "DEPRECATED_SelectInput";
const DEPRECATED_SelectInput = ({ children , ...props })=>{
    if (hasBaseSelect(props)) {
        return React__default.createElement(SelectInputAs, {
            ...props,
            as: undefined
        }, children);
    }
    if (hasChip(props)) {
        return React__default.createElement(SelectInputAs, {
            ...props,
            as: Chip
        }, children);
    }
    if (!hasChip(props)) {
        return React__default.createElement(SelectInputAs, {
            ...props,
            as: Button
        }, children);
    }
    return null;
};
DEPRECATED_SelectInput.dataId = "data-hpy-web-select-field";
DEPRECATED_SelectInput.displayName = "DEPRECATED_SelectInput";

export { DEPRECATED_SelectInput, DEPRECATED_SelectOption, NO_VALUE };
